import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import FabBar from "@/design/components/FabBar";
import MasterListVirtualized from "@/design/components/MasterListVirtualized";
import { disableSearch, enableSearch } from "@/store/reducers/ui";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viMusicPause, viPlus } from "@sippy-platform/valkyrie";

import { useMediaQuery, useTheme } from "@mui/material";
import Button from "@mui/material/Button";

function MusicOnHoldList() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchesMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const musicOnHoldStore = useSelector((state) => state.domain.musicOnHolds);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const progressBar = useSelector((state) => state.ui.progressBar);

  // Search every time musicOnHoldList or searchQuery changes
  const searchResults = useSearch(musicOnHoldStore.list, searchValue, ["name"]);

  useEffect(() => {
    toggleLoadingState(true);
    Promise.resolve(Api.getMusicOnHolds()).finally(toggleLoadingState);
  }, []);

  // Open first item or new if the viewport is large enough
  useEffect(() => {
    if (!id && matchesMdUp && musicOnHoldStore.meta.lastFetched) {
      if (musicOnHoldStore.list.length >= 1) {
        navigate(`${musicOnHoldStore.list[0].id}`);
      } else {
        navigate("new");
      }
    }
  }, [id, matchesMdUp, navigate, musicOnHoldStore.list, musicOnHoldStore.meta.lastFetched]);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  return (
    <>
      <MasterListVirtualized
        fabBar
        title="Music on Hold"
        aria-label="Music on Hold list"
        id="moh-list"
        list={searchResults}
        listItemProps={{
          primary: (x) => x.name,
          secondary: (x) => (x.system ? "System" : undefined),
          to: (x) => `../${x.id}`,
          selected: (x) => x.id === Number(id),
          icon: viMusicPause,
        }}
        hideContentLoader={musicOnHoldStore.meta.count >= 1 && searchResults.length >= 1}
        contentLoaderProps={{
          count: musicOnHoldStore.meta.count,
          loading: progressBar,
          noItems: "You don't have any music on holds yet",
          noResults: "No music on holds in your current query",
          fetching: "Getting your music on holds",
          icon: viMusicPause,
        }}
      />
      <FabBar>
        <Button variant="contained" component={NavLink} to="new" startIcon={<Valkyrie icon={viPlus} />}>
          Add
        </Button>
      </FabBar>
    </>
  );
}

export default MusicOnHoldList;
